import React from 'react';
import { LEGAL } from '../legalInfo';
import { PublicHeader, PublicFooter } from './LandingPage';

// The three public legal pages.
//
// No draft or "not yet reviewed" notice, in any build. One used to print to
// the public on every page, including the privacy policy an App Store
// reviewer opens and a customer's procurement team reads, where it invited
// exactly the doubt it was trying to be honest about. That the wording still
// wants a solicitor's eye is true and tracked in LAUNCH.md - a to-do belongs
// on a to-do list, not on the page.
function Mail({ address }) {
  return <a href={`mailto:${address}`}>{address}</a>;
}

function LegalShell({ title, updated, children }) {
  return (
    <div className="public">
      <PublicHeader showLogin={false} />
      <main className="public-main page-narrow">
        <h1>{title}</h1>
        <p className="muted" style={{ marginTop: -6 }}>Last updated: {updated}</p>
        <div className="card legal-body">{children}</div>
      </main>
      <PublicFooter />
    </div>
  );
}

function H({ children }) { return <h2 className="legal-h">{children}</h2>; }
function P({ children }) { return <p>{children}</p>; }
function Ul({ items }) { return <ul className="legal-list">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>; }

export function PrivacyPolicyPage() {
  return (
    <LegalShell title="Privacy Policy" updated="14 September 2026">
      <P>
        {LEGAL.companyName} ("we", "us", "our") provides Your Data Your AI, a construction document platform: your
        own drawings, specifications, standards, contracts, programmes and site photographs, made searchable and
        assessed against the documents that govern them. This policy explains what personal data we collect, why,
        and the choices and rights you have. It is written to reflect the UK GDPR, the Data Protection Act 2018,
        and — where our customers or their users are based in the EEA — the EU GDPR.
      </P>

      <H>1. Who we are</H>
      <P>
        {LEGAL.companyName} is the data controller for account and platform-usage data described below. Registered
        in {LEGAL.registeredIn}, company number {LEGAL.companyNumber}, registered office {LEGAL.registeredAddress}.
        Data protection enquiries: <Mail address={LEGAL.privacyEmail} />.
      </P>
      <P>
        Where an organisation (our "customer") uses Your Data Your AI with their own team, project data uploaded to
        that organisation's workspace is generally controlled by that organisation, with {LEGAL.companyName} acting
        as a data processor on their behalf for that content. This policy covers both roles.
      </P>

      <H>2. What we collect</H>
      <Ul items={[
        'Account and identity data — name, email address, employer/organisation and role, obtained via Microsoft Entra ID sign-in.',
        'Usage data — sign-in times, actions taken in the app, device and browser information, and IP address.',
        "Content you or your organisation upload — drawings, photos, comments and documents. This may itself contain personal data if your team includes it (for example a name in a comment); that is controlled by your organisation, not by us.",
        'Billing data — plan, billing contact and transaction history. Full card details are handled directly by Stripe; we do not receive or store them.',
        'Support communications — anything you send us when you contact support.',
      ]} />

      <H>3. Why we process it, and our legal basis</H>
      <Ul items={[
        'To provide the service you have signed up for (performance of a contract).',
        'To operate optional AI-assisted features you or your organisation turn on — see Section 4 (performance of a contract / legitimate interest, and consent where a feature specifically requires it).',
        'To bill and administer subscriptions (performance of a contract; legal obligation for tax and accounting records).',
        'To keep the service secure and working reliably, and to improve it (legitimate interest).',
        'To respond to support requests and send service-related communications (performance of a contract / legitimate interest).',
      ]} />

      <H>4. AI-assisted features</H>
      <P>
        Some features — drawing comparison, photo analysis, custom agents — send relevant content such as a drawing,
        a photograph or extracted text to an AI provider to generate a result. Which provider processes that request
        depends on configuration:
      </P>
      <Ul items={[
        'If your organisation has connected its own AI provider account ("bring your own key"), requests for your organisation use that account and are subject to that provider’s own terms and data-handling policies.',
        'Otherwise, requests use a shared default provider we configure, under our agreement with that provider.',
      ]} />
      <P>
        Each organisation's AI connections and requests are kept isolated from other organisations — one customer's
        data is never included in another customer's AI requests. We do not use your content to train our own models.
      </P>

      <H>5. Who we share data with</H>
      <P>We use a small number of sub-processors to run the service:</P>
      <Ul items={[
        'Microsoft Azure — application hosting and data storage.',
        'Stripe — payment processing for subscriptions.',
        "Your organisation's configured AI provider(s), or our default provider — see Section 4.",
        'Procore — only if your organisation enables the Procore integration, and only for the data that integration explicitly syncs.',
        'Service providers who help us send transactional email, such as invitations and notifications.',
      ]} />
      <P>We do not sell personal data, and we do not share it with third parties for their own marketing purposes.</P>

      <H>6. International transfers</H>
      <P>
        Some of the providers above may process data outside the UK/EEA, for example in the United States. Where that
        happens we rely on appropriate safeguards such as Standard Contractual Clauses or an applicable adequacy
        decision.
      </P>

      <H>7. How long we keep data</H>
      <P>
        We retain your organisation's uploaded content and platform data for as long as your subscription is active.
        After it ends, content is retained for 90 days so an account can be reinstated or its data exported, and is
        then deleted. Billing and transaction records are kept for six years, as UK tax law requires. Your
        organisation can ask us to delete its content sooner, and we will do so except where a record must be kept
        to meet that legal obligation.
      </P>

      <H>8. Security</H>
      <P>
        Data is encrypted in transit. Stored credentials such as connected AI provider API keys are encrypted at
        rest. Each organisation's data is isolated from other organisations. Access to production systems is
        restricted to authorised personnel.
      </P>

      <H>9. Your rights</H>
      <P>Subject to applicable law, you have the right to:</P>
      <Ul items={[
        'Access the personal data we hold about you.',
        'Correct inaccurate data.',
        'Request deletion of your data.',
        'Restrict or object to certain processing.',
        'Receive your data in a portable format.',
        'Withdraw consent, where processing is based on consent.',
      ]} />
      <P>
        To exercise any of these, contact <Mail address={LEGAL.privacyEmail} />. If you are not satisfied with our
        response you can complain to the UK Information Commissioner's Office (ico.org.uk) or your local supervisory
        authority.
        {/* The number is printed only once there IS one. A reader's rights do
            not depend on it, and a page that says "[placeholder]" where a
            registration number belongs looks worse than one that simply names
            the regulator - it advertises the gap. */}
        {LEGAL.icoRegistrationNumber ? ` Our ICO registration number: ${LEGAL.icoRegistrationNumber}.` : ''}
      </P>

      <H>10. Children</H>
      <P>Your Data Your AI is a business tool and is not directed at, or knowingly used by, children.</P>

      <H>11. Changes to this policy</H>
      <P>We will update the "last updated" date above when this policy changes, and post the revised version here.</P>

      <H>12. Contact us</H>
      <P>{LEGAL.companyName}, {LEGAL.registeredAddress} — <Mail address={LEGAL.privacyEmail} /></P>
    </LegalShell>
  );
}

export function CookiePolicyPage() {
  return (
    <LegalShell title="Cookie Policy" updated="14 September 2026">
      <P>
        This policy explains how {LEGAL.companyName} uses cookies and similar technologies on Your Data Your AI, in
        line with the UK Privacy and Electronic Communications Regulations (PECR) and UK/EU GDPR.
      </P>

      <H>1. What cookies are</H>
      <P>
        Cookies are small text files stored on your device that let a website remember information between requests,
        such as keeping you signed in.
      </P>

      <H>2. Cookies we use</H>
      <P>
        Your Data Your AI itself sets no cookies. It uses <strong>strictly necessary</strong> browser session
        storage instead — your sign-in token and the organisation you are working in, held only for the life of the
        browser session and cleared when you close it. PECR treats that the same as a cookie, which is why it is set
        out here. It is required for the service to function and cannot be switched off within the app; without it,
        sign-in will not work.
      </P>
      <P>
        As of the date above we do not use analytics, advertising or marketing cookies or trackers. If that changes,
        this policy will be updated first and, where required by law, we will ask for your consent.
      </P>

      <H>3. Third-party cookies</H>
      <P>
        Signing in uses Microsoft Entra ID and subscription checkout uses Stripe. Both may set their own cookies
        during those flows, governed by Microsoft's and Stripe's own cookie and privacy policies rather than ours.
      </P>

      <H>4. Managing cookies</H>
      <P>
        Most browsers let you view, delete and block cookies via their settings. Since Your Data Your AI's own
        cookies are strictly necessary, blocking them will prevent you from signing in or staying signed in.
      </P>

      <H>5. Changes to this policy</H>
      <P>We will update the "last updated" date above when this policy changes.</P>

      <H>6. Contact us</H>
      <P>{LEGAL.companyName}, {LEGAL.registeredAddress} — <Mail address={LEGAL.privacyEmail} /></P>
    </LegalShell>
  );
}

export function TermsOfServicePage() {
  return (
    <LegalShell title="Terms of Service" updated="14 September 2026">
      <P>
        These terms govern access to and use of Your Data Your AI (the "Service"), provided by {LEGAL.companyName}
        ("we", "us"). By creating an account, signing in, or using the Service, you and the organisation you
        represent ("Customer", "you") agree to these terms. If you do not agree, do not use the Service.
      </P>

      <H>1. The Service</H>
      <P>
        Your Data Your AI is a construction document platform: document, drawing and photograph management, a
        drawing register and drawing-comparison tooling, search and question-answering across your own uploaded
        documents, AI-assisted analysis and custom agents including assessment of work against technical standards
        you supply, reporting, and optional third-party integrations such as Procore. Features available to you
        depend on your subscription plan.
      </P>

      <H>2. Accounts and access</H>
      <Ul items={[
        "You sign in via Microsoft Entra ID. You are responsible for restricting access to your organisation's Entra tenant and for the actions of users you invite into the workspace.",
        'You must provide accurate organisation and billing information and keep it up to date.',
        'We may suspend or terminate access to a workspace that materially breaches these terms, poses a security risk, or has an unpaid overdue invoice — we will give notice where reasonably possible.',
      ]} />

      <H>3. Subscriptions and billing</H>
      <Ul items={[
        'Paid plans are billed in advance on a recurring basis via Stripe. Fees are exclusive of tax unless stated otherwise.',
        'You can upgrade, downgrade or cancel at any time; changes take effect as described at checkout or in-app. Except where required by law, fees already paid are non-refundable.',
        'If a payment fails and is not resolved within a reasonable period, we may suspend access to paid features until payment is made.',
        'Enterprise plans may be subject to a separate order form or agreement, which takes priority over these terms where it conflicts.',
      ]} />

      <H>4. Your content</H>
      <Ul items={[
        'You (or your organisation) own the drawings, photos, documents and other content you upload to your workspace ("Customer Content"). We claim no ownership over it.',
        'You grant us a licence to host, process and display Customer Content solely to provide and support the Service to you, including passing relevant content to an AI provider when you or your organisation use an AI-assisted feature.',
        'You are responsible for having the necessary rights and permissions for the content you upload, including any third-party or personal data it contains.',
        "Each organisation's workspace and data is kept isolated from other organisations using the Service.",
      ]} />

      <H>5. Acceptable use</H>
      <P>You agree not to:</P>
      <Ul items={[
        "Use the Service to store or transmit unlawful content, or in a way that infringes a third party's rights.",
        "Attempt to bypass tenant isolation, access another organisation's workspace or data, or probe or scan the Service for vulnerabilities without authorisation.",
        'Reverse-engineer, resell, or use the Service to build a directly competing product.',
        "Interfere with the Service's normal operation, including excessive automated requests that degrade it for others.",
      ]} />

      <H>6. AI-assisted features</H>
      <P>
        Some features send relevant content to an AI provider to generate a result — either a provider your
        organisation has connected with its own account, or our shared default provider. AI-generated output such as
        drawing comparisons, extracted revisions, analysis and chat responses is provided as a helpful aid and may
        occasionally be inaccurate or incomplete. You remain responsible for reviewing and verifying AI-assisted
        output before relying on it for decisions of consequence. See our Privacy Policy, Section 4, for how AI
        requests are handled and isolated.
      </P>

      <H>7. Third-party integrations</H>
      <P>
        Optional integrations let you connect a third-party account to sync data into or out of Your Data Your AI.
        Your use of a connected third-party service is subject to that provider's own terms. We are not responsible
        for the availability, accuracy or content of third-party services.
      </P>

      <H>8. Intellectual property</H>
      <P>
        The Service, including its software, design and branding, is owned by {LEGAL.companyName} or our licensors
        and is protected by intellectual property laws. Except for the limited right to use the Service under these
        terms, no rights are granted to you in it.
      </P>

      <H>9. Availability and support</H>
      <P>
        We aim to keep the Service available and reliable but do not guarantee uninterrupted access. We may perform
        maintenance, and features may change as the Service evolves. Support is provided
        via <Mail address={LEGAL.supportEmail} />.
      </P>

      <H>10. Disclaimers and limitation of liability</H>
      <P>
        The Service is provided "as is" without warranties of any kind beyond those that cannot be excluded by law.
        To the fullest extent permitted by law, {LEGAL.companyName} is not liable for indirect, incidental or
        consequential losses arising from use of the Service, and our total liability for any claim is limited to
        the fees paid for the Service in the 12 months before the claim arose. Nothing in these terms limits
        liability for death, personal injury caused by negligence, or fraud.
      </P>

      <H>11. Termination</H>
      <P>
        Either party may terminate a subscription as described in Section 3 or, for a material uncured breach, with
        written notice. On termination your right to access the Service ends; we will retain Customer Content for a
        limited period to allow export or as described in our Privacy Policy, after which it may be deleted.
      </P>

      <H>12. Governing law</H>
      <P>
        These terms are governed by the laws of {LEGAL.registeredIn}, and the courts of {LEGAL.registeredIn} have
        exclusive jurisdiction over any dispute arising from them, unless mandatory local law gives you rights to
        bring a claim elsewhere.
      </P>

      <H>13. Changes to these terms</H>
      <P>
        We will update the "last updated" date above when these terms change, and post the revised version here.
        Continued use of the Service after a change constitutes acceptance of the updated terms.
      </P>

      <H>14. Contact us</H>
      <P>{LEGAL.companyName}, {LEGAL.registeredAddress} — <Mail address={LEGAL.contactEmail} /></P>
    </LegalShell>
  );
}
